import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "predictive-search-results",
  class PredictiveSearchResults extends WYATTElement {
    constructor() {
      super();

      this.resultsContainer = this.querySelector("[data-results]");
      this.itemTemplate = this.querySelector('[data-template="item"]');
      this.abortController = null;
      this.requestSequence = 0;

      this.subscribe(
        WYATT.PubSub.events.searchInputChange,
        this.handleSearchInputChange.bind(this),
      );
    }

    // Beacon autocomplete events are product-typed, and only the products
    // sub-grid (not the collections/pages/articles/queries instances) carries
    // products on the dropdown.
    get isProductsAutocomplete() {
      return (
        this.config.api === "autocomplete" &&
        this.config.siteId === WYATT.config.searchspring.siteIds.products
      );
    }

    get requestUrl() {
      const params = new URLSearchParams({
        siteId: this.config.siteId,
        resultsFormat: "json",
        resultsPerPage: this.config.resultsPerPage,
        q: this.query,
        domain: window.location.href,
        pageLoadId: WYATT.searchspring.tracking.pageLoadId,
        sessionId: WYATT.searchspring.tracking.ssSessionIdNamespace,
        userId: WYATT.searchspring.tracking.ssUserId,
      });

      // Required for Searchspring to return a beacon-compatible responseId,
      // which the autocomplete beacon events reference. Only the products
      // dropdown emits beacon events, so scope it there.
      if (this.isProductsAutocomplete) {
        params.set("beacon", "true");
      }

      return [
        "https://",
        `${this.config.siteId}.`,
        `${WYATT.config.searchspring.endpoints[this.config.api]}?`,
        params.toString(),
      ].join("");
    }

    handleSearchInputChange(event) {
      event.preventDefault();

      this.query = event.detail;

      this.fetchResults();
    }

    async fetchResults() {
      const render =
        this.config.api === "suggest"
          ? this.renderSuggestions.bind(this)
          : this.renderResults.bind(this);

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.requestSequence += 1;
      const requestId = this.requestSequence;

      try {
        const response = await fetch(this.requestUrl, {
          ...WYATT.config.searchspring.options.get,
          signal: this.abortController.signal,
        });
        const data = await response.json();

        if (requestId !== this.requestSequence) return;

        this.dataset.ssResponseId = (data && data.responseId) || "";

        if (this.isProductsAutocomplete && WYATT.beacon) {
          WYATT.beacon.autocomplete.render({
            responseId: this.dataset.ssResponseId,
            results: (data.results || []).map(WYATT.beacon.normalizeResult),
          });
        }

        render(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          throw error;
        }
      } finally {
        if (requestId === this.requestSequence) {
          this.abortController = null;
        }
      }
    }

    renderSuggestions({ alternatives, suggested }) {
      if (alternatives.length === 0 || !suggested) {
        this.resultsContainer.innerHTML = `<p>${WYATT.t.predictiveSearchResults.noResults}</p>`;
        return;
      }

      this.resultsContainer.innerHTML = "";

      const suggestedItem =
        this.itemTemplate.content.cloneNode(true).firstElementChild;

      suggestedItem.dataset.query = JSON.stringify(suggested);
      this.resultsContainer.appendChild(suggestedItem);

      alternatives.forEach((alternative) => {
        const alternativeItem =
          this.itemTemplate.content.cloneNode(true).firstElementChild;

        alternativeItem.dataset.query = JSON.stringify(alternative);
        this.resultsContainer.appendChild(alternativeItem);
      });
    }

    renderResults(data) {
      const { results } = data;

      if (this.impressionObserver) {
        this.impressionObserver.disconnect();
        this.impressionObserver = null;
      }

      if (this.config.redirect) {
        this.publish(WYATT.PubSub.events.redirectFetched, data);
      }

      if (this.config.brands) {
        this.publish(
          WYATT.PubSub.events.predictiveSearchBrandsFetched,
          data?.facets,
        );
      }

      if (results.length === 0) {
        this.resultsContainer.innerHTML = `<p>${WYATT.t.predictiveSearchResults.noResults}</p>`;
        return;
      }

      this.resultsContainer.innerHTML = "";

      results.forEach((result) => {
        const item =
          this.itemTemplate.content.cloneNode(true).firstElementChild;

        item.dataset.result = JSON.stringify(result);

        if (this.isProductsAutocomplete) {
          item.dataset.ssResult = JSON.stringify({
            uid: result.uid,
            parentId: result.parentId ?? result.uid,
            sku: result.sku,
          });
        }

        this.resultsContainer.appendChild(item);
      });

      if (this.isProductsAutocomplete && WYATT.beacon) {
        this.impressionObserver = WYATT.beacon.observeImpressions(
          this,
          "autocomplete",
        );
      }
    }
  },
);
